import { SATError, SATErrorType, errorConverter } from "@sadtx/sat-error";
import { StoreHttpError } from "./data/store-http-client";

/**
 * 스토어 서버(7000xx) 에러 → 화면 안내
 *
 * 공통 변환기(@sadtx/sat-error)는 7000xx 코드를 모르기 때문에 Unknown 모델("알 수 없는 오류", 서버 메시지 폐기)로 떨어뜨린다.
 * 여기서는 서버가 내려준 message·nextAction·tossCode 를 보존해 사용자에게 "무엇이 실패했고 다음에 무엇을 하면 되는지"를 보여준다.
 * nextAction 규약은 서버 store.exception.ts 의 PaymentFailNextAction 과 같다.
 */
export type PaymentFailNextAction = "RETRY" | "CHANGE_METHOD" | "RESTART" | "CHECK_HISTORY" | "CLAIM_LATER";

export class StoreApiError extends SATError {
  readonly httpStatus: number;
  readonly nextAction?: PaymentFailNextAction;
  readonly tossCode?: string;

  constructor(
    httpStatus: number,
    errorCode: string,
    message: string,
    nextAction?: PaymentFailNextAction,
    tossCode?: string
  ) {
    super(SATErrorType.UnknownError, message, errorCode);
    this.name = STORE_ERROR_TITLE[errorCode] ?? "요청 처리 실패";
    this.httpStatus = httpStatus;
    this.nextAction = nextAction;
    this.tossCode = tossCode;
  }
}

/** 에러 코드별 모달 제목 (서버 store.exception.ts 와 1:1) */
const STORE_ERROR_TITLE: Record<string, string> = {
  "700001": "판매 중인 상품이 아닙니다",
  "700002": "주문 정보를 찾을 수 없습니다",
  "700003": "결제 금액 불일치",
  "700004": "결제 승인 실패",
  "700005": "결제 완료 주문이 아닙니다",
  "700006": "이미 처리된 주문",
  "700007": "소속 병원 정보 없음",
  "700008": "권한 없음",
  "700009": "환불 기한 초과",
  "700010": "환불 증서 오류",
  "700011": "환불 불가 상태",
  "700012": "환불 요청 중복",
  "700013": "환불 요청 없음",
  "700014": "등록되지 않은 이용권이 있습니다",
  "700015": "결제 시간 만료",
  "700016": "결제수단 거절",
  "700017": "결제사 일시 오류",
  "700018": "결제 상태 확인 불가",
  "700019": "이용권 발급 준비 실패",
};

/** 서버가 nextAction 을 주지 않은 코드의 기본 다음 행동 */
const DEFAULT_NEXT_ACTION: Partial<Record<string, PaymentFailNextAction>> = {
  "700014": "CHECK_HISTORY",
  "700003": "RESTART",
};

const isStoreErrorBody = (
  data: unknown
): data is { errorCode: string; message?: string; nextAction?: PaymentFailNextAction; tossCode?: string } =>
  typeof data === "object" &&
  data !== null &&
  typeof (data as { errorCode?: unknown }).errorCode === "string" &&
  (data as { errorCode: string }).errorCode.startsWith("7");

/**
 * 스토어 API 호출 실패 → SATError. 7000xx 는 서버 메시지·nextAction 을 보존한 StoreApiError,
 * 그 외(401·네트워크 등)는 공통 변환기 결과를 그대로 쓴다.
 */
export const toStoreError = (error: unknown): SATError => {
  if (error instanceof StoreHttpError && isStoreErrorBody(error.response.data)) {
    const body = error.response.data;
    return new StoreApiError(
      error.response.status,
      body.errorCode,
      body.message ?? "요청을 처리하지 못했습니다.",
      body.nextAction ?? DEFAULT_NEXT_ACTION[body.errorCode],
      body.tossCode
    );
  }
  return errorConverter.convertError(error);
};

/** 화면 안내에 필요한 요약 — 제목·본문·다음 행동 */
export const describeStoreError = (
  error: SATError
): { title: string; message: string; nextAction: PaymentFailNextAction | null } => {
  if (error instanceof StoreApiError) {
    return { title: error.name, message: error.message, nextAction: error.nextAction ?? null };
  }
  return { title: error.name, message: error.message, nextAction: null };
};

/** 결제 내역 확인이 다음 행동인 경우 (재결제 금지) */
export const shouldGoToPaymentHistory = (nextAction: PaymentFailNextAction | null): boolean =>
  nextAction === "CHECK_HISTORY" || nextAction === "CLAIM_LATER";
